// import { useMemo } from 'react'; // Not used
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { DotsSixVertical, X } from 'shared/icons';
import { COLUMN_CATEGORIES, PREDEFINED_COLUMNS } from '../../constants/presets';

/**
 * ColumnSelector
 *
 * Componente para selecao e ordenacao de colunas.
 * Permite selecionar colunas pre-definidas e reordenar colunas selecionadas via drag & drop.
 */
const ColumnSelector = ({ selectedColumns, onColumnsChange }) => {
    // Agrupar colunas por categoria
    const columnsByCategory = Object.entries(PREDEFINED_COLUMNS).reduce((acc, [key, col]) => {
        const category = col.category;
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push({ ...col, key });
        return acc;
    }, {});

    // Ordenar categorias
    const sortedCategories = Object.keys(columnsByCategory).sort((a, b) => {
        return COLUMN_CATEGORIES[a].order - COLUMN_CATEGORIES[b].order;
    });

    // Verificar se uma coluna esta selecionada
    const isColumnSelected = (key) => {
        return selectedColumns.some((col) => col.type === 'predefined' && col.key === key);
    };

    // Toggle selecao de coluna
    const handleToggleColumn = (columnKey) => {
        const column = PREDEFINED_COLUMNS[columnKey];

        if (isColumnSelected(columnKey)) {
            // Remover coluna
            const filtered = selectedColumns.filter((col) => !(col.type === 'predefined' && col.key === columnKey));
            onColumnsChange(filtered);
        } else {
            // Adicionar coluna
            const newColumn = {
                id: `col-${columnKey}-${Date.now()}`,
                type: 'predefined',
                ...column,
            };
            onColumnsChange([...selectedColumns, newColumn]);
        }
    };

    // Verificar se ha pelo menos uma coluna de Informacao
    // const hasInfoColumn = () => {
    //   return selectedColumns.some(col =>
    //     col.type === 'predefined' && col.category === 'identification'
    //   );
    // };

    // Contar colunas de Informacao
    const countInfoColumns = () => {
        return selectedColumns.filter((col) => col.type === 'predefined' && col.category === 'identification').length;
    };

    // Remover coluna da lista de selecionadas
    const handleRemoveColumn = (columnId) => {
        const column = selectedColumns.find((col) => col.id === columnId);

        // Se e coluna de Informacao e e a ultima, impedir remocao
        if (column && column.category === 'identification' && countInfoColumns() === 1) {
            alert('Pelo menos uma coluna de Informacao deve estar selecionada.');
            return;
        }

        const filtered = selectedColumns.filter((col) => col.id !== columnId);
        onColumnsChange(filtered);
    };

    // Separar colunas de Informacao das demais
    const infoColumns = selectedColumns.filter((col) => col.type === 'predefined' && col.category === 'identification');

    const otherColumns = selectedColumns.filter(
        (col) => !(col.type === 'predefined' && col.category === 'identification'),
    );

    // Handler de drag & drop com separacao de areas
    const handleDragEnd = (result) => {
        if (!result.destination) {
            return;
        }

        const sourceArea = result.source.droppableId;
        const destArea = result.destination.droppableId;

        // Se tentou mover coluna de Informacao para area de outras colunas, impedir
        if (sourceArea === 'info-columns' && destArea === 'other-columns') {
            return;
        }

        // Se tentou mover outra coluna para area de Informacao, impedir
        if (sourceArea === 'other-columns' && destArea === 'info-columns') {
            return;
        }

        // Reordenar dentro da mesma area
        const items = sourceArea === 'info-columns' ? [...infoColumns] : [...otherColumns];
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        // Recombinar arrays mantendo ordem: info primeiro, depois outras
        const newColumns = sourceArea === 'info-columns' ? [...items, ...otherColumns] : [...infoColumns, ...items];

        onColumnsChange(newColumns);
    };

    // Obter label da coluna
    const getColumnLabel = (column) => {
        if (column.type === 'predefined') {
            return column.label;
        }
        return column.name;
    };

    return (
        <div className="space-y-6">
            {/* Colunas Disponiveis */}
            <div>
                <h3 className="text-sm font-semibold text-black dark:text-white mb-3">Colunas Disponiveis</h3>
                <div className="border border-mono-200 dark:border-mono-800 rounded-lg overflow-hidden max-h-80 overflow-y-auto">
                    {sortedCategories.map((category) => (
                        <div key={category}>
                            <div className="px-4 py-2 text-xs font-semibold text-mono-600 dark:text-mono-400 uppercase tracking-wider bg-mono-50 dark:bg-mono-900 sticky top-0">
                                {COLUMN_CATEGORIES[category].label}
                            </div>
                            <div className="divide-y divide-mono-200 dark:divide-mono-800">
                                {columnsByCategory[category].map((column) => (
                                    <label
                                        key={column.key}
                                        className="flex items-center gap-3 px-4 py-3 hover:bg-mono-50 dark:hover:bg-mono-900 cursor-pointer transition-colors"
                                        title={column.description}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isColumnSelected(column.key)}
                                            onChange={() => handleToggleColumn(column.key)}
                                            className="w-4 h-4 text-blue-600 dark:text-blue-400 border-mono-300 dark:border-mono-700 rounded focus:ring-blue-500 dark:focus:ring-blue-400"
                                        />
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-black dark:text-white">
                                                {column.label}
                                            </div>
                                            <div className="text-xs text-mono-500 dark:text-mono-500">
                                                {column.description}
                                            </div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Colunas Selecionadas */}
            <div>
                <h3 className="text-sm font-semibold text-black dark:text-white mb-3">
                    Colunas Selecionadas ({selectedColumns.length})
                </h3>

                {selectedColumns.length === 0 ? (
                    <div className="border border-mono-200 dark:border-mono-800 rounded-lg p-8 text-center text-sm text-mono-500 dark:text-mono-500">
                        Nenhuma coluna selecionada.
                        <br />
                        Selecione colunas acima para comecar.
                    </div>
                ) : (
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <div className="space-y-4">
                            {/* Area de colunas de Informacao */}
                            {infoColumns.length > 0 && (
                                <div>
                                    <h4 className="text-xs font-semibold text-mono-600 dark:text-mono-400 uppercase tracking-wider mb-2">
                                        Colunas de Informacao (Obrigatorias)
                                    </h4>
                                    <Droppable droppableId="info-columns">
                                        {(provided, snapshot) => (
                                            <div
                                                {...provided.droppableProps}
                                                ref={provided.innerRef}
                                                className={`border border-mono-200 dark:border-mono-800 rounded-lg overflow-hidden ${
                                                    snapshot.isDraggingOver ? 'bg-blue-50 dark:bg-blue-950' : ''
                                                }`}
                                            >
                                                {infoColumns.map((column, index) => (
                                                    <Draggable key={column.id} draggableId={column.id} index={index}>
                                                        {(provided, snapshot) => (
                                                            <div
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                className={`flex items-center gap-3 px-4 py-3 bg-white dark:bg-mono-950 border-b border-mono-200 dark:border-mono-800 last:border-b-0 ${
                                                                    snapshot.isDragging ? 'shadow-lg' : ''
                                                                }`}
                                                            >
                                                                {/* Drag handle */}
                                                                <div
                                                                    {...provided.dragHandleProps}
                                                                    className="cursor-grab active:cursor-grabbing text-mono-400 dark:text-mono-600"
                                                                >
                                                                    <DotsSixVertical size={20} weight="bold" />
                                                                </div>

                                                                {/* Column info */}
                                                                <div className="flex-1">
                                                                    <div className="text-sm font-medium text-black dark:text-white">
                                                                        {getColumnLabel(column)}
                                                                    </div>
                                                                </div>

                                                                {/* Badge de tipo */}
                                                                <span className="px-2 py-0.5 text-xs font-medium rounded text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950">
                                                                    Informacao
                                                                </span>

                                                                {/* Botao remover */}
                                                                <button
                                                                    onClick={() => handleRemoveColumn(column.id)}
                                                                    className="p-1.5 text-mono-600 dark:text-mono-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950 rounded transition-colors"
                                                                    title="Remover coluna"
                                                                >
                                                                    <X size={16} weight="bold" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </div>
                            )}

                            {/* Area de outras colunas */}
                            {otherColumns.length > 0 && (
                                <div>
                                    <h4 className="text-xs font-semibold text-mono-600 dark:text-mono-400 uppercase tracking-wider mb-2">
                                        Outras Colunas
                                    </h4>
                                    <Droppable droppableId="other-columns">
                                        {(provided, snapshot) => (
                                            <div
                                                {...provided.droppableProps}
                                                ref={provided.innerRef}
                                                className={`border border-mono-200 dark:border-mono-800 rounded-lg overflow-hidden ${
                                                    snapshot.isDraggingOver ? 'bg-blue-50 dark:bg-blue-950' : ''
                                                }`}
                                            >
                                                {otherColumns.map((column, index) => (
                                                    <Draggable key={column.id} draggableId={column.id} index={index}>
                                                        {(provided, snapshot) => (
                                                            <div
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                className={`flex items-center gap-3 px-4 py-3 bg-white dark:bg-mono-950 border-b border-mono-200 dark:border-mono-800 last:border-b-0 ${
                                                                    snapshot.isDragging ? 'shadow-lg' : ''
                                                                }`}
                                                            >
                                                                {/* Drag handle */}
                                                                <div
                                                                    {...provided.dragHandleProps}
                                                                    className="cursor-grab active:cursor-grabbing text-mono-400 dark:text-mono-600"
                                                                >
                                                                    <DotsSixVertical size={20} weight="bold" />
                                                                </div>

                                                                {/* Column info */}
                                                                <div className="flex-1">
                                                                    <div className="text-sm font-medium text-black dark:text-white">
                                                                        {getColumnLabel(column)}
                                                                    </div>
                                                                    {column.type === 'custom' && (
                                                                        <div className="text-xs text-mono-500 dark:text-mono-500 font-mono mt-0.5">
                                                                            {column.expression}
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Badge de tipo */}
                                                                <span
                                                                    className={`px-2 py-0.5 text-xs font-medium rounded ${
                                                                        column.type === 'custom'
                                                                            ? 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-950'
                                                                            : 'text-mono-600 dark:text-mono-400 bg-mono-100 dark:bg-mono-800'
                                                                    }`}
                                                                >
                                                                    {column.type === 'custom' ? 'Custom' : 'Padrao'}
                                                                </span>

                                                                {/* Botao remover */}
                                                                <button
                                                                    onClick={() => handleRemoveColumn(column.id)}
                                                                    className="p-1.5 text-mono-600 dark:text-mono-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950 rounded transition-colors"
                                                                    title="Remover coluna"
                                                                >
                                                                    <X size={16} weight="bold" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </div>
                            )}
                        </div>
                    </DragDropContext>
                )}
            </div>
        </div>
    );
};

export default ColumnSelector;
